import React, { useCallback, FC } from 'react'
import { UseFormMethods, useWatch } from 'react-hook-form'
import { stringifyUrl } from 'query-string'
import ControlledAutoTaggingInput from 'transfix-form/components/ControlledAutoTaggingInput'
import ControlledAutocompleteCarrier from 'transfix-form/components/ControlledAutocompleteCarrier'
import ControlledCheckbox from 'transfix-form/components/ControlledCheckbox'
import ControlledCurrencyInput from 'transfix-form/components/ControlledCurrencyInput'
import ControlledPhoneInput from 'transfix-form/components/ControlledPhoneInput'
import ControlledTextField from 'transfix-form/components/ControlledTextInput'
import AddIcon from 'transfix-icons/build/AddIcon'
import { Button } from 'transfix-ui/components/Button'
import Grid from 'transfix-ui/core/Grid'
import Typography from 'transfix-ui/core/Typography'
import { IUser } from 'types/graphqlTypes'
import CarrierStatusCard from 'apps/marketplace/components/CarrierStatusCard'
import ShipmentDetailsCard from 'apps/marketplace/components/ShipmentDetailsCard'
import ControlledCarrierUserAutosuggest from 'apps/marketplace/pages/shipment-details/components/forms/controlled/ControlledCarrierUserAutosuggest'
import { TCarrierOptionSchema, getErrorMessage } from '../../schema'
import { ICarrierOptionContact } from '../../types'
import { getCarrierAutocompleteConfig } from '../../utils'
import RateCardFormSection from '../RateCardFormSection'
import SourceFormSection from '../SourceFormSection'

interface ICarrierOptionFormBodyProps
  extends Pick<UseFormMethods<TCarrierOptionSchema>, 'control' | 'setValue' | 'errors'> {
  defaultContact?: ICarrierOptionContact | null
  isEditMode: boolean
  shipmentId: string
}

const CarrierOptionFormBody: FC<ICarrierOptionFormBodyProps> = ({
  control,
  defaultContact,
  errors,
  isEditMode,
  setValue,
  shipmentId,
}) => {
  // Carrier
  const carrierApplication = useWatch<TCarrierOptionSchema['carrierApplication']>({
    control,
    name: 'carrierApplication',
  })
  const { id: carrierId } = carrierApplication || {}

  // Carrier Contact Name
  const carrierContactName = useWatch<TCarrierOptionSchema['carrierContactName']>({
    control,
    name: 'carrierContactName',
  })

  const userSelectHandler = useCallback(
    (user: IUser | null) => {
      const { fullName, phone } = user || {}
      setValue('carrierContactName', fullName, { shouldValidate: true })
      if (phone) {
        setValue('carrierContactPhone', phone, { shouldValidate: true })
      }
    },
    [setValue]
  )

  const userInputChangeHandler = useCallback(
    (value: string) => {
      setValue('carrierContactName', value, { shouldValidate: true })
    },
    [setValue]
  )

  const addNewUserHandler = useCallback(() => {
    const {
      dotNumber: carrierDotNumber,
      id: carrierId,
      mcNumber: carrierMcNumber,
      name: carrierName,
    } = carrierApplication || {}
    const addUserUrl = stringifyUrl({
      url: `${__ROOT__}/users/new`,
      query: {
        orgType: 'carrier',
        orgId: carrierId || '',
        orgName: carrierName || '',
        orgMcNumber: carrierMcNumber || '',
        orgDotNumber: carrierDotNumber || '',
      },
    })
    window.open(addUserUrl, '_blank')
  }, [carrierApplication])

  return (
    <Grid container spacing={0}>
      <Grid item md={12}>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <ShipmentDetailsCard shipmentId={shipmentId} />
          </Grid>
          <Grid item xs={12}>
            <hr />
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={3}>
              <Grid item md={6}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant='subtitle1'>Carrier Information</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <ControlledAutocompleteCarrier
                      name='carrierApplication'
                      control={control}
                      AutocompleteCarrierProps={{
                        label: '',
                        renderInputType: 'withIcon',
                        inputIcon: 'Search',
                        placeholder: '',
                        getOptionsConfig: getCarrierAutocompleteConfig,
                        disabled: isEditMode,
                        dataTest: 'option-form-carrier',
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <div data-test='option-form-carrier-user'>
                      {carrierId ? (
                        <ControlledCarrierUserAutosuggest
                          name='carrierContactUserId'
                          control={control}
                          carrierId={carrierId}
                          user={defaultContact}
                          defaultInputValue={carrierContactName}
                          onUserChange={userSelectHandler}
                          onInputChange={userInputChangeHandler}
                          AutocompleteProps={{
                            label: 'Dispatcher Name',
                            renderInputType: 'withIcon',
                            inputIcon: 'People',
                            /** The following properties are needed for freeSolo */
                            autoHighlight: false,
                            autoSelect: false,
                            autoComplete: false,
                            clearOnBlur: false,
                            clearOnEscape: false,
                            selectOnFocus: false,
                            freeSolo: true,
                          }}
                        />
                      ) : (
                        <ControlledTextField
                          control={control}
                          name='carrierContactName'
                          TextInputProps={{
                            label: 'Dispatcher Name',
                            icon: 'people',
                            large: true,
                          }}
                        />
                      )}
                      <Button
                        variant='text'
                        startIcon={<AddIcon />}
                        onClick={addNewUserHandler}
                        noPadding>
                        Add new user
                      </Button>
                    </div>
                  </Grid>
                  <Grid item xs={12}>
                    <Grid container spacing={1}>
                      <Grid item md={8}>
                        <ControlledPhoneInput
                          control={control}
                          name='carrierContactPhone'
                          PhoneInputProps={{
                            label: 'Dispatcher Phone',
                            clearable: true,
                            large: true,
                            required: true,
                            dataTest: 'option-form-phone',
                            invalid: !!getErrorMessage(errors, 'carrierContactPhone'),
                            validationMessage: getErrorMessage(errors, 'carrierContactPhone'),
                          }}
                        />
                      </Grid>
                      <Grid item md={4}>
                        <ControlledTextField
                          control={control}
                          name='carrierContactPhoneExt'
                          TextInputProps={{
                            label: 'Phone Ext',
                            dataTest: 'option-form-phone-ext',
                            placeholder: 'Ext.',
                            large: true,
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item md={6}>
                <CarrierStatusCard carrierApplicationId={carrierId} shipmentId={shipmentId} />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <hr />
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={3}>
              <Grid item md={6}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant='subtitle1'>Add Rate</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <div>
                      <ControlledCurrencyInput
                        name='amount'
                        control={control}
                        CurrencyInputProps={{
                          placeholder: '$',
                          label: 'Rate',
                          required: true,
                          hideRequiredLabel: true,
                          large: true,
                          dataTest: 'option-form-rate-amount',
                          invalid: !!getErrorMessage(errors, 'amount'),
                          validationMessage: getErrorMessage(errors, 'amount'),
                        }}
                      />
                    </div>
                  </Grid>
                  {!isEditMode && (
                    <Grid item xs={12}>
                      <ControlledAutoTaggingInput
                        name='note'
                        control={control}
                        defaultValue=''
                        AutoTaggingInputProps={{
                          className: 'expandable-rounded',
                          rows: 3,
                          label: 'Notes',
                          placeholder: 'Add notes about this rate',
                          dataTest: 'option-form-note',
                        }}
                      />
                    </Grid>
                  )}
                  {!isEditMode && (
                    <>
                      <Grid item xs={12}>
                        <SourceFormSection control={control} errors={errors} />
                      </Grid>
                      <Grid item xs={12}>
                        <ControlledCheckbox
                          control={control}
                          name='nonBookable'
                          CheckboxProps={{
                            label: 'Mark as non-bookable option',
                          }}
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
              </Grid>
              <Grid item md={6}>
                <RateCardFormSection
                  isEditMode={isEditMode}
                  shipmentId={shipmentId}
                  control={control}
                  setValue={setValue}
                  errors={errors}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )
}

export default CarrierOptionFormBody
